/**
 * Dynamic Flow Engine Component
 * 
 * This is the main component that:
 * 1. Fetches screens from the backend API
 * 2. Renders screens using generic renderers
 * 3. Handles navigation (next/back)
 * 4. Tracks answers and progress
 * 
 * All screen content comes from the backend - no hardcoded text.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ScreenRenderer, Screen } from './GenericScreenRenderer';
import { Sidebar, buildCategoriesFromScreens } from './Sidebar';
import { post, get } from '../../shared/apiClient';
import { capture, group, buildTaxCalculationPayload } from '../../shared/posthog';

interface DynamicFlowEngineProps {
  country?: string | null;
  taxYear?: number;
  onComplete?: (summary: any) => void;
  /** Start at a specific screen (e.g. general_deductions). From URL ?startAt=... when REACT_APP_FLOW_ALLOW_START_AT is not "false". */
  startAtScreenId?: string;
  /** Region for Spain (e.g. Andalusia). From URL ?region=...; backend maps to FIGMA value (e.g. Andalucía) for show_if. */
  region?: string;
}

interface ScreenListItem {
  screen_id: string;
  title: string;
  screen_type: string;
  category: string;
  country: string;
  order: number;
}

interface StartFlowResponse {
  session_id: string;
  country?: string | null;
  screen: Screen;
  visited_screen_ids?: string[];
  initial_answers?: Record<string, any>;
}

interface NextScreenResponse {
  success: boolean;
  country?: string | null;
  next_screen?: Screen;
  completed?: boolean;
  summary?: any;
  tax_calculation?: Record<string, unknown>;
  error?: string;
}

interface BackScreenResponse {
  success: boolean;
  country?: string | null;
  screen?: Screen;
  error?: string;
}

interface ResumeSessionResponse {
  session_id: string;
  screen: Screen;
  answers: Record<string, any>;
  visited_screen_ids: string[];
  country?: string | null;
}

const FLOW_SESSION_KEY = 'flow_session_id';
const FLOW_COUNTRY_KEY = 'flow_country';
const FLOW_TAX_YEAR_KEY = 'flow_tax_year';

interface FlowState {
  sessionId: string | null;
  currentScreen: Screen | null;
  answers: Record<string, any>;
  isLoading: boolean;
  error: string | null;
  isComplete: boolean;
  summary: any | null;
  allScreens: ScreenListItem[];
  visitedScreenIds: string[];
  sessionCountry: string | null;
}

const DynamicFlowEngine: React.FC<DynamicFlowEngineProps> = ({
  country = null,
  taxYear = 2024,
  onComplete,
  startAtScreenId,
  region,
}) => {
  const [state, setState] = useState<FlowState>({
    sessionId: null,
    currentScreen: null,
    answers: {},
    isLoading: true,
    error: null,
    isComplete: false,
    summary: null,
    allScreens: [],
    visitedScreenIds: [],
    sessionCountry: null,
  });

  const lastActionRef = useRef<'start' | 'next' | 'back' | 'resume'>('start');

  // Start the flow (new session)
  const startFlow = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const startBody: { country?: string | null; tax_year: number; start_at_screen_id?: string; region?: string } = {
        country: country ?? null,
        tax_year: taxYear,
      };
      if (startAtScreenId?.trim()) {
        startBody.start_at_screen_id = startAtScreenId.trim();
      }
      if (region?.trim()) {
        startBody.region = region.trim();
      }

      // Fetch flow start and screen list in parallel
      const screensUrl = country ? `/flow/screens?country=${country}` : `/flow/screens?country=BOTH`;
      const [flowResponse, screensResponse] = await Promise.all([
        post<typeof startBody, StartFlowResponse>('/flow/start', startBody),
        get<{ total: number; screens: ScreenListItem[] }>(screensUrl)
      ]);
      
      // Persist session so we can resume after refresh
      try {
        localStorage.setItem(FLOW_SESSION_KEY, flowResponse.data.session_id);
        if (country) {
          localStorage.setItem(FLOW_COUNTRY_KEY, country);
        } else {
          localStorage.removeItem(FLOW_COUNTRY_KEY);
        }
        localStorage.setItem(FLOW_TAX_YEAR_KEY, String(taxYear));
      } catch (_) { /* ignore */ }
      
      // Pre-populate answers from current_value in questions (from backend's stored answers)
      const prePopulatedAnswers: Record<string, any> = {};
      flowResponse.data.screen.questions?.forEach((q: any) => {
        if (q.current_value !== undefined && q.current_value !== null) {
          prePopulatedAnswers[q.id] = q.current_value;
        }
      });
      
      const firstScreenId = flowResponse.data.screen.screen_id;
      const visitedIds = flowResponse.data.visited_screen_ids?.length
        ? flowResponse.data.visited_screen_ids
        : [firstScreenId];
      const sessionId = flowResponse.data.session_id;
      const sessionCountry = (flowResponse.data.country as string | null) ?? null;

      lastActionRef.current = 'start';
      group('flow_session', sessionId, { country: sessionCountry ?? country, tax_year: taxYear });
      capture('flow_started', {
        session_id: sessionId,
        country: sessionCountry ?? country ?? undefined,
        tax_year: taxYear,
        start_at_screen_id: startAtScreenId?.trim() || undefined,
        region: region?.trim() || undefined,
        first_screen_id: firstScreenId,
      });

      setState(prev => ({
        ...prev,
        sessionId,
        currentScreen: flowResponse.data.screen,
        // Ensure `country`/`tax_year` (and `region` when provided) exist in answers so question-level `show_if`
        // conditions that depend on them work correctly.
        answers: {
          ...prePopulatedAnswers,
          ...(flowResponse.data.initial_answers || {}),
          country: (flowResponse.data.country as any) ?? country ?? null,
          tax_year: taxYear,
        },
        allScreens: screensResponse.data.screens || [],
        visitedScreenIds: visitedIds,
        sessionCountry,
        isLoading: false,
      }));
    } catch (error) {
      const err = error as { status?: number; message?: string };
      const statusCode = typeof err?.status === 'number' ? err.status : undefined;
      const reason = statusCode === 503 ? '503' : statusCode === 404 ? '404' : statusCode ? 'api' : 'network';
      const message = error instanceof Error ? error.message : 'Failed to start flow';
      capture('flow_start_failed', { reason, message, status_code: statusCode });
      capture('flow_error_displayed', { source: 'start', message });
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, [country, taxYear, startAtScreenId, region]);

  // Submit answers and go to next screen
  const handleNext = useCallback(async () => {
    if (!state.sessionId) return;

    const currentScreenId = state.currentScreen?.screen_id;
    const questionIds = state.currentScreen?.questions?.map((q: { id: string }) => q.id) ?? [];
    // Include full answers for debugging; remove or gate by env for production if needed.
    capture('flow_step_submitted', {
      session_id: state.sessionId,
      screen_id: currentScreenId,
      answers_count: Object.keys(state.answers).length,
      question_ids: questionIds,
      answers: state.answers,
    });

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const response = await post<{ session_id: string; answers: Record<string, any> }, NextScreenResponse>(
        '/flow/next',
        {
          session_id: state.sessionId,
          answers: state.answers,
        }
      );
      
      if (response.data.completed) {
        const summary = response.data.summary;
        const screensVisited = summary?.screens_visited ?? state.visitedScreenIds.length;
        const sessionCountry = (response.data.country as string | null) ?? state.sessionCountry;
        capture('flow_completed', {
          session_id: state.sessionId,
          country: sessionCountry ?? undefined,
          tax_year: taxYear,
          screens_visited: screensVisited,
          has_tax_calculation: !!response.data.tax_calculation,
          answers: summary?.answers ?? state.answers,
        });
        if (response.data.tax_calculation) {
          const taxPayload = buildTaxCalculationPayload(
            response.data.tax_calculation,
            state.sessionId,
            sessionCountry,
            taxYear
          ) as Record<string, unknown>;
          capture('tax_calculation_received', {
            ...taxPayload,
            tax_calculation_full: response.data.tax_calculation,
          });
        }
        try {
          localStorage.removeItem(FLOW_SESSION_KEY);
          localStorage.removeItem(FLOW_COUNTRY_KEY);
          localStorage.removeItem(FLOW_TAX_YEAR_KEY);
        } catch (_) { /* ignore */ }
        setState(prev => ({
          ...prev,
          isLoading: false,
          isComplete: true,
          summary: response.data.summary,
          sessionCountry: (response.data.country as any) ?? prev.sessionCountry,
        }));
        onComplete?.(response.data.summary);
      } else if (response.data.next_screen) {
        lastActionRef.current = 'next';
        const nextScreenId = response.data.next_screen.screen_id;
        capture('flow_step_success', {
          session_id: state.sessionId,
          from_screen_id: currentScreenId,
          next_screen_id: nextScreenId,
          screens_visited: state.visitedScreenIds.length + (state.visitedScreenIds.includes(nextScreenId) ? 0 : 1),
        });
        // Pre-populate from backend current_value; merge into existing answers so we keep
        // all collected data (needed for tax summary source_field lookups and deductions).
        const prePopulatedAnswers: Record<string, any> = {};
        response.data.next_screen.questions?.forEach((q: any) => {
          if (q.current_value !== undefined && q.current_value !== null) {
            prePopulatedAnswers[q.id] = q.current_value;
          }
        });

        const isCoOwnershipScreen = nextScreenId === 'co_ownership_details' || nextScreenId.startsWith('co_ownership_details_');

        setState(prev => {
          // When entering a new co-ownership screen, drop previous co-owner form data so each context (property/rental/sold/spain_income) starts blank.
          const baseAnswers = isCoOwnershipScreen && prev.answers
            ? Object.fromEntries(
                Object.entries(prev.answers).filter(
                  ([k]) => !/^coowner_\d+_/.test(k) && k !== 'add_coowner'
                )
              )
            : { ...prev.answers };
          return {
            ...prev,
            currentScreen: response.data.next_screen!,
            answers: {
              ...baseAnswers,
              ...prePopulatedAnswers,
              country: (response.data.country as any) ?? prev.sessionCountry ?? prev.answers?.country ?? null,
              tax_year: prev.answers?.tax_year ?? taxYear,
            },
            visitedScreenIds: prev.visitedScreenIds.includes(nextScreenId) 
              ? prev.visitedScreenIds 
              : [...prev.visitedScreenIds, nextScreenId],
            sessionCountry: (response.data.country as any) ?? prev.sessionCountry,
            isLoading: false,
          };
        });
      } else {
        const errMsg = response.data.error || 'Unknown error';
        capture('flow_step_failed', {
          session_id: state.sessionId,
          screen_id: currentScreenId,
          error_type: 'validation',
          message: errMsg,
          answers: state.answers,
        });
        capture('flow_error_displayed', { source: 'next', message: errMsg });
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: errMsg,
        }));
      }
    } catch (error) {
      const err = error as { status?: number; message?: string };
      const statusCode = typeof err?.status === 'number' ? err.status : undefined;
      const message = error instanceof Error ? error.message : 'Failed to submit answers';
      capture('flow_step_failed', {
        session_id: state.sessionId,
        screen_id: currentScreenId,
        error_type: 'api',
        message,
        status_code: statusCode,
        answers: state.answers,
      });
      capture('flow_error_displayed', { source: 'next', message });
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, [state.sessionId, state.answers, state.currentScreen, state.visitedScreenIds, state.sessionCountry, onComplete, taxYear]);

  // Go back to previous screen
  const handleBack = useCallback(async () => {
    if (!state.sessionId) return;
    const fromScreenId = state.currentScreen?.screen_id;

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const response = await post<{ session_id: string }, BackScreenResponse>(
        '/flow/back',
        { session_id: state.sessionId }
      );
      
      if (response.data.success && response.data.screen) {
        const prevScreenId = response.data.screen.screen_id;
        lastActionRef.current = 'back';
        capture('flow_back', {
          session_id: state.sessionId,
          from_screen_id: fromScreenId,
          to_screen_id: prevScreenId,
        });
        // Pre-populate from backend; merge so we keep all answers (e.g. for review screens).
        const prePopulatedAnswers: Record<string, any> = {};
        response.data.screen.questions?.forEach((q: any) => {
          if (q.current_value !== undefined && q.current_value !== null) {
            prePopulatedAnswers[q.id] = q.current_value;
          }
        });
        const isParentOfCoOwnership =
          prevScreenId === 'property_personal_use_spain' ||
          prevScreenId === 'rental_property_spain' ||
          prevScreenId === 'sold_property_spain' ||
          prevScreenId === 'income_from_spain';

        setState(prev => {
          const baseAnswers = isParentOfCoOwnership && prev.answers
            ? Object.fromEntries(
                Object.entries(prev.answers).filter(
                  ([k]) =>
                    !/^coowner_\d+_/.test(k) &&
                    k !== 'add_coowner' &&
                    k !== 'coownership_context_source' &&
                    k !== 'coownership_context_label' &&
                    k !== 'coownership_context_user_pct'
                )
              )
            : { ...prev.answers };
          return {
            ...prev,
            currentScreen: response.data.screen!,
            answers: {
              ...baseAnswers,
              ...prePopulatedAnswers,
              country: (response.data.country as any) ?? prev.sessionCountry ?? prev.answers?.country ?? null,
              tax_year: prev.answers?.tax_year ?? taxYear,
            },
            isLoading: false,
            sessionCountry: (response.data.country as any) ?? prev.sessionCountry,
          };
        });
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      const err = error as { status?: number };
      const statusCode = typeof err?.status === 'number' ? err.status : undefined;
      const message = error instanceof Error ? error.message : 'Failed to go back';
      capture('flow_back_failed', { session_id: state.sessionId, message, status_code: statusCode });
      capture('flow_error_displayed', { source: 'back', message });
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, [state.sessionId, state.currentScreen, taxYear]);

  // Handle answer change
  const handleAnswerChange = useCallback((questionId: string, value: any) => {
    setState(prev => {
      const nextAnswers = { ...prev.answers, [questionId]: value };
      // When user changes ownership % on a parent screen, clear co-ownership context and that context's co-owner data so the flow resets (no stale 100% warning; co-owner form will be fresh when they continue).
      const ownershipPctMatch = questionId.match(/^(property|rental|sold|spain_income)_\d+_ownership_percentage$/);
      if (ownershipPctMatch) {
        const source = ownershipPctMatch[1];
        const toRemove = [
          'coownership_context_source',
          'coownership_context_label',
          'coownership_context_user_pct',
          'add_coowner',
        ];
        toRemove.forEach(k => delete nextAnswers[k]);
        Object.keys(nextAnswers).forEach(k => {
          if (k.startsWith(`coowner_${source}_`) || /^coowner_\d+_/.test(k)) delete nextAnswers[k];
        });
      }
      return {
        ...prev,
        answers: nextAnswers,
      };
    });
  }, []);

  // Try to resume from localStorage, otherwise start new flow.
  // When URL has startAt (or start_at), always start fresh so the user lands on that screen.
  const tryResumeOrStart = useCallback(async () => {
    if (startAtScreenId?.trim()) {
      try {
        localStorage.removeItem(FLOW_SESSION_KEY);
        localStorage.removeItem(FLOW_COUNTRY_KEY);
        localStorage.removeItem(FLOW_TAX_YEAR_KEY);
      } catch (_) { /* ignore */ }
      startFlow();
      return;
    }
    const storedSessionId = localStorage.getItem(FLOW_SESSION_KEY);
    if (!storedSessionId) {
      startFlow();
      return;
    }
    capture('flow_resume_attempted', { had_stored_session: true, session_id: storedSessionId });
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      const res = await get<ResumeSessionResponse>(`/flow/session/${storedSessionId}`);
      const resumeCountry = res.data.country ?? country;
      const screensUrl = resumeCountry ? `/flow/screens?country=${resumeCountry}` : `/flow/screens?country=BOTH`;
      const screensRes = await get<{ screens: ScreenListItem[] }>(screensUrl);
      lastActionRef.current = 'resume';
      group('flow_session', res.data.session_id, { country: resumeCountry, tax_year: taxYear });
      capture('flow_resume_success', {
        session_id: res.data.session_id,
        screen_id: res.data.screen?.screen_id,
        visited_screen_count: res.data.visited_screen_ids?.length ?? 0,
      });
      setState(prev => ({
        ...prev,
        sessionId: res.data.session_id,
        currentScreen: res.data.screen,
        answers: res.data.answers ?? {},
        visitedScreenIds: res.data.visited_screen_ids ?? [],
        allScreens: screensRes.data?.screens ?? [],
        sessionCountry: (res.data.country as any) ?? null,
        isLoading: false,
      }));
    } catch (err) {
      const statusCode = err && typeof err === 'object' && 'status' in err ? (err as { status: number }).status : undefined;
      capture('flow_resume_failed', {
        session_id: storedSessionId,
        reason: statusCode === 404 ? '404' : 'network',
        message: err instanceof Error ? err.message : undefined,
        status_code: statusCode,
      });
      try {
        localStorage.removeItem(FLOW_SESSION_KEY);
        localStorage.removeItem(FLOW_COUNTRY_KEY);
        localStorage.removeItem(FLOW_TAX_YEAR_KEY);
      } catch (_) { /* ignore */ }
      startFlow();
    }
  }, [country, startFlow, startAtScreenId, taxYear]);

  useEffect(() => {
    tryResumeOrStart();
  }, [tryResumeOrStart]);

  // Fire screen_viewed when the user sees a new screen (after start, next, back, resume).
  useEffect(() => {
    if (!state.sessionId || !state.currentScreen || state.isLoading || state.error) return;
    const screen = state.currentScreen;
    const nav = (screen.navigation ?? {}) as { current_index?: number; total_screens?: number };
    const progress = (screen.progress ?? {}) as { percentage?: number };
    capture('screen_viewed', {
      session_id: state.sessionId,
      screen_id: screen.screen_id,
      screen_type: screen.screen_type ?? undefined,
      category: screen.category ?? undefined,
      step_index: typeof nav.current_index === 'number' ? nav.current_index : undefined,
      step_total: typeof nav.total_screens === 'number' ? nav.total_screens : undefined,
      progress_percentage: typeof progress.percentage === 'number' ? progress.percentage : undefined,
      from_action: lastActionRef.current,
      visited_screen_count: state.visitedScreenIds.length,
    });
  }, [state.sessionId, state.currentScreen, state.isLoading, state.error, state.visitedScreenIds.length]);

  // Manual session reset or retry after error: clear saved session and start a new flow
  const resetSession = useCallback((reason: 'error' | 'manual' = 'manual') => {
    capture('flow_reset', { session_id: state.sessionId ?? undefined, reason });
    try {
      localStorage.removeItem(FLOW_SESSION_KEY);
      localStorage.removeItem(FLOW_COUNTRY_KEY);
      localStorage.removeItem(FLOW_TAX_YEAR_KEY);
    } catch (_) { /* ignore */ }
    startFlow();
  }, [startFlow, state.sessionId]);

  // Loading state
  if (state.isLoading && !state.currentScreen) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#07A2A0] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (state.error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-white rounded-xl p-8 shadow-lg text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-gray-600 mb-6">{state.error}</p>
          <button
            onClick={() => resetSession('error')}
            className="px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg hover:bg-[#069694] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Completion state
  if (state.isComplete && state.summary) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="max-w-xl w-full bg-white rounded-2xl p-10 shadow-xl text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Flow Complete!</h1>
          <p className="text-gray-500 mb-8">
            You've completed all {state.summary.screens_visited} screens.
          </p>
          
          <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-semibold text-gray-900 mb-4">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Country:</span>
                <span className="font-medium">{state.summary.country}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tax Year:</span>
                <span className="font-medium">{state.summary.tax_year}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Screens Visited:</span>
                <span className="font-medium">{state.summary.screens_visited}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Answers Collected:</span>
                <span className="font-medium">{Object.keys(state.summary.answers).length}</span>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => {
              capture('flow_reset', { session_id: state.sessionId ?? undefined, reason: 'manual' });
              startFlow();
            }}
            className="px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg hover:bg-[#069694] transition-colors"
          >
            Start Over
          </button>
        </div>
      </div>
    );
  }

  // No screen state
  if (!state.currentScreen) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">No screen available</p>
      </div>
    );
  }

  // Build sidebar categories from screen data
  const sidebarCategories = buildCategoriesFromScreens(
    state.allScreens,
    state.visitedScreenIds,
    state.currentScreen.category,
    state.currentScreen.screen_id
  );

  // Main render: use grid layout (Chrome-friendly height calculation) + explicit scroll area
  return (
    <div className="grid grid-cols-[auto_1fr] h-full min-h-0 bg-gray-50 overflow-hidden">
      {/* Sidebar - fixed width */}
      <Sidebar
        categories={sidebarCategories}
        currentCategory={state.currentScreen.category}
        currentScreenId={state.currentScreen.screen_id}
        onSubcategoryClick={(screenId) => {
          // Navigate to specific screen (only allow if already visited)
          if (state.visitedScreenIds.includes(screenId)) {
            // For now, just log - full navigation would require backend support
            console.log('Navigate to screen:', screenId);
          }
        }}
      />

      {/* Main Content - grid so content row gets definite height in Chrome */}
      <div className="grid grid-rows-[auto_1fr] min-h-0 min-w-0">
        {/* Header - fixed height row */}
        <header className="shrink-0 bg-white px-6 py-4 flex items-center justify-between border-b border-gray-200 shadow-sm">
          {/* Progress info */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              Step {state.currentScreen.navigation.current_index + 1} of {state.currentScreen.navigation.total_screens}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="flex-1 max-w-md mx-8">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#07A2A0] transition-all duration-300 rounded-full"
                style={{ width: `${state.currentScreen.progress.percentage}%` }}
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4 text-sm text-gray-600">
            {state.sessionCountry ? (
              <span className="px-3 py-1 bg-gray-100 rounded-full">
                {state.sessionCountry === 'ES' ? '🇪🇸 Spain' : '🇺🇸 USA'}
              </span>
            ) : (
              <span className="px-3 py-1 bg-gray-100 rounded-full">
                Country: not selected
              </span>
            )}
            <span className="px-3 py-1 bg-gray-100 rounded-full">
              {taxYear}
            </span>
            <button
              type="button"
              onClick={() => resetSession('manual')}
              className="text-gray-500 hover:text-gray-700 underline text-xs"
              title="Clear progress and start from the beginning"
            >
              Start over
            </button>
          </div>
        </header>

        {/* Screen Content - grid row 1fr gets remaining height; overflow-auto for scroll */}
        <div className="min-h-0 overflow-y-auto overflow-x-hidden bg-gray-50">
          <ScreenRenderer
          screen={state.currentScreen}
          answers={state.answers}
          onAnswerChange={handleAnswerChange}
          onNext={handleNext}
          onBack={handleBack}
          isSubmitting={state.isLoading}
            userCountry={state.sessionCountry || ''}
          />
        </div>
      </div>
    </div>
  );
};

export default DynamicFlowEngine;
